import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import {
    Dialog,
    DialogContent,
    LinearProgress,
    makeStyles,
    Theme,
    Typography
} from '@material-ui/core'
import React from 'react'
import ManageUnmappedDialogTitle from './DialogTitle'

const useStyles = makeStyles((theme: Theme) => ({
    dialog: {
        '& .MuiDialog-paper': {
            minWidth: pxToRem(400),
            maxWidth: pxToRem(500),
        },
    },
    dialogContent: {
        padding: theme.spacing(3, 3, 4),
        textAlign: 'center',
    },
    subtitle: {
        fontSize: pxToRem(16),
        fontWeight: 500,
        color: '#333333',
        marginBottom: theme.spacing(3),
    },
    progressContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: theme.spacing(2),
    },
    progressText: {
        fontSize: pxToRem(16),
        color: '#666666',
        marginBottom: theme.spacing(2),
    },
    progressBar: {
        width: '100%',
        height: pxToRem(8),
        borderRadius: pxToRem(4),
        backgroundColor: '#E0E0E0',
        '& .MuiLinearProgress-bar': {
            backgroundColor: '#1899D4',
            borderRadius: pxToRem(4),
        },
    },
}))

/**
 * Props for the ClashDetectionModal component
 */
export interface ClashDetectionModalProps {
    /** Whether the modal is open */
    open: boolean
    /** Optional title for the modal */
    title?: string
    /** Optional subtitle for the modal */
    subtitle?: string
    /** Optional text to display during progress */
    progressText?: string
}

/**
 * Modal that displays progress while checking for clashes during adding an unmapped product listing into a family/pt.
 * 
 * Shows an indeterminate progress bar with customizable title and progress text.
 * This modal cannot be dismissed by the user (no backdrop click or escape key).
 * 
 * @param props - The component props
 * @returns The ClashDetectionModal component
 */
const ClashDetectionModal: React.FC<ClashDetectionModalProps> = ({
    open,
    title = 'Manage Unmapped Product',
    subtitle = 'Create a New Family',
    progressText = 'Checking for Clashes',
}) => {
    const classes = useStyles()

    return (
        <Dialog
            open={open}
            onClose={() => {}} // Completely disable closing for progress modal
            className={classes.dialog}
            aria-labelledby="clash-detection-dialog-title"
        >
            <ManageUnmappedDialogTitle
            >
                {title}
            </ManageUnmappedDialogTitle>
            <DialogContent className={classes.dialogContent}>
                <div className={classes.subtitle}>
                    {subtitle}
                </div>

                <div className={classes.progressContainer}>
                    <Typography className={classes.progressText}>
                        {progressText}
                    </Typography>

                    <LinearProgress
                        className={classes.progressBar}
                        variant="indeterminate"
                    />
                </div>
            </DialogContent>
        </Dialog>
    )
}

export default ClashDetectionModal